import { IWebsiteBuilderNodeBase, IWebsiteBuilderNodeDesignStyles, TWebsiteBuilderNode } from "../websiteBuilderNode";
import { websiteBuilderElementValid } from "../element/websiteBuilderElement";
import { isWebsiteBuilderContent, IWebsiteBuilderContent } from "./websiteBuilderContent";
import { isWebsiteBuilderHeader, IWebsiteBuilderHeader } from "./websiteBuilderHeader";
import { isWebsiteBuilderFooter, IWebsiteBuilderFooter } from "./websiteBuilderFooter";
import { websiteBuilderContainerValid } from "../container/websiteBuilderContainer";
import { CSSProperties } from "react";
import { websiteBuilderNormalizeValue, websiteBuilderNormalizeDatabaseId, websiteBuilderNormalizeHexColor, websiteBuilderNormalizePercent } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { TWebsiteBuilderSectionEntity } from "../websiteBuilderSectionEntity";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "../websiteBuilderNodeGeometry";
import { WEBSITE_BUILDER_CURRENT_SCHEMA_VERSION } from "../../schema/websiteBuilderCurrentSchemaVersion";
import { TWebsiteBuilderDevice, WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_DESKTOP_WIDTH, WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_MOBILE_WIDTH, WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_TABLET_WIDTH } from "../../website/shared/device";
import { TWebsiteBuilderNormalizeTraverse } from "../../schema/normalization/websiteBuilderNormalizeEditor";
import { TWebsiteBuilderHideableNodeVisibility, websiteBuilderNormalizeHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { IWebsiteBuilderRoot } from "../root/websiteBuilderRoot";

export {
  WEBSITE_BUILDER_SECTION_BACKGROUND_DARKEN_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_BACKGROUND_MEDIA_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_PARALLAX_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_PARALLAX_MEDIA_CLASS_NAME,
} from "../../website/shared/sectionBackground";

export const WEBSITE_BUILDER_MINIMUM_SECTION_HEIGHT = 75;
export const WEBSITE_BUILDER_SECTION_BORDER_COLOR = "#EAECF0";
export const WEBSITE_BUILDER_SECTION_PARALLAX_MIN_AMOUNT = 0;
export const WEBSITE_BUILDER_SECTION_PARALLAX_MAX_AMOUNT = 100;
export const WEBSITE_BUILDER_SECTION_PARALLAX_DEFAULT_AMOUNT = 50;

const WEBSITE_BUILDER_SECTION_PARALLAX_MIN_SHIFT_PERCENT = 12;
const WEBSITE_BUILDER_SECTION_PARALLAX_MAX_SHIFT_PERCENT = 36;

export type TWebsiteBuilderSectionBackgroundSize = "cover" | "contain";
export type TWebsiteBuilderSectionBackgroundMediaType = "image" | "video";

function isWebsiteBuilderSectionBackgroundSize(value: string): value is TWebsiteBuilderSectionBackgroundSize {
  return value === "cover" || value === "contain";
}

function isWebsiteBuilderSectionBackgroundMediaType(value: string): value is TWebsiteBuilderSectionBackgroundMediaType {
  return value === "image" || value === "video";
}

export interface IWebsiteBuilderSectionBackground {
  mediaId: number;
  src: string;
  mediaType: TWebsiteBuilderSectionBackgroundMediaType;
  color: string;
  size: TWebsiteBuilderSectionBackgroundSize;
  xPercent: number;
  yPercent: number;
  darken: boolean;
  darkenPercent: number;
  parallax: boolean;
  parallaxAmount: number;
}

export function isWebsiteBuilderSectionBackgroundVideo(background: IWebsiteBuilderSectionBackground): boolean {
  return background.mediaType === "video" && Boolean(background.src);
}

interface IWebsiteBuilderSectionLayoutData {
  height: number;
}

export type TWebsiteBuilderSectionLayout = {
  [K in TWebsiteBuilderDevice]: IWebsiteBuilderSectionLayoutData;
}

export interface IWebsiteBuilderSectionDataBase {
  type: string;
  layout: TWebsiteBuilderSectionLayout;
  background: IWebsiteBuilderSectionBackground;
}

export interface IWebsiteBuilderSectionBase extends IWebsiteBuilderNodeBase {
  type: "section";
  section: IWebsiteBuilderSectionDataBase;
  children: TWebsiteBuilderSectionEntity[];
}

export type TWebsiteBuilderSection = IWebsiteBuilderHeader | IWebsiteBuilderContent | IWebsiteBuilderFooter;

export type TWebsiteBuilderSectionData = TWebsiteBuilderSection["section"];
export type TWebsiteBuilderSectionType = TWebsiteBuilderSectionData["type"];

export type TWebsiteBuilderMainSection = IWebsiteBuilderContent;

export function isWebsiteBuilderSection(node: any): node is TWebsiteBuilderSection {
  return node?.type === "section";
}

export function isWebsiteBuilderMainSection(node: TWebsiteBuilderNode): node is TWebsiteBuilderMainSection {
  return isWebsiteBuilderSection(node) && node.section.type === "content";
}

export function websiteBuilderSectionValid(node: any): node is TWebsiteBuilderSection {
  const partialNode: Partial<TWebsiteBuilderSection> = node;

  const isValidLayoutData = (data: IWebsiteBuilderSectionLayoutData) => {
    return data?.height != null;
  };
  
  return (
    partialNode != null &&
    partialNode.id != null &&
    partialNode.type === "section" &&
    partialNode.section != null &&
    (
      partialNode.section.type === "header" ||
      partialNode.section.type === "content" ||
      partialNode.section.type === "footer"
    ) &&
    (
      (partialNode.section.type !== "header" && partialNode.section.type !== "footer") ||
      partialNode.schemaVersion === WEBSITE_BUILDER_CURRENT_SCHEMA_VERSION
    ) &&
    isValidLayoutData(partialNode.section.layout?.desktop) &&
    isValidLayoutData(partialNode.section.layout?.tablet) &&
    isValidLayoutData(partialNode.section.layout?.mobile)
  );
}

export function websiteBuilderGenerateDefaultSectionBackground(): IWebsiteBuilderSectionBackground {
  return {
    mediaId: null,
    src: null,
    mediaType: "image",
    color: null,
    size: "cover",
    xPercent: 0,
    yPercent: 0,
    darken: false,
    darkenPercent: 0,
    parallax: false,
    parallaxAmount: WEBSITE_BUILDER_SECTION_PARALLAX_DEFAULT_AMOUNT,
  };
}

export function websiteBuilderGenerateSectionParallaxShift(amount: number): { shift: string; travel: string } {
  const shiftPercent = WEBSITE_BUILDER_SECTION_PARALLAX_MIN_SHIFT_PERCENT
    + (amount / WEBSITE_BUILDER_SECTION_PARALLAX_MAX_AMOUNT)
    * (WEBSITE_BUILDER_SECTION_PARALLAX_MAX_SHIFT_PERCENT - WEBSITE_BUILDER_SECTION_PARALLAX_MIN_SHIFT_PERCENT);
  const shift = shiftPercent / 100;
  const travelPercent = (shift / (1 + (2 * shift))) * 100;

  return {
    shift: `${shiftPercent}%`,
    travel: `${travelPercent}%`,
  };
}

export function isWebsiteBuilderSectionParallax(node: TWebsiteBuilderSection): boolean {
  const background = node.section.background;

  return (
    isWebsiteBuilderMainSection(node) &&
    background.parallax &&
    background.size === "cover" &&
    Boolean(background.src) &&
    !isWebsiteBuilderSectionBackgroundVideo(background)
  );
}

type TWebsiteBuilderSectionDataBaseContent = Omit<IWebsiteBuilderSectionDataBase, "type">;

function websiteBuilderNormalizeSectionDataBaseContent(sectionDataBaseContent: TWebsiteBuilderSectionDataBaseContent, children: TWebsiteBuilderSectionEntity[]): TWebsiteBuilderSectionDataBaseContent {
  function normalizeHeight(layout: TWebsiteBuilderSectionLayout, device: TWebsiteBuilderDevice) {        
    const heightCandidates = children.map(child => {
      if (child?.type === "container") {
        const y = child.container?.layout?.[device]?.position?.y ?? 0;
        const height = child.container?.layout?.[device]?.dimensions?.height ?? 0;

        return y + height;
      } else if (child?.type === "element") {
        const y = child.element?.layout?.[device]?.position?.y ?? 0;
        const height = child.element?.layout?.[device]?.dimensions?.height ?? 0;

        return y + height;
      } else {
        return 0;
      }
    });

    const largestHeightCandidate = heightCandidates.reduce((prev, next) => prev > next ? prev : next, 0);

    const rawHeight = typeof layout[device].height !== "number" ? 0 : layout[device].height;

    return Math.max(largestHeightCandidate, rawHeight, WEBSITE_BUILDER_MINIMUM_SECTION_HEIGHT);
  }
  
  const normalizedLayout: TWebsiteBuilderSectionLayout = {
    desktop: {
      height: normalizeHeight(sectionDataBaseContent.layout, "desktop"),
    },
    tablet: {
      height: normalizeHeight(sectionDataBaseContent.layout, "tablet"),
    },
    mobile: {
      height: normalizeHeight(sectionDataBaseContent.layout, "mobile"),
    },
  };

  const defaultBackground = websiteBuilderGenerateDefaultSectionBackground();

  let normalizedBackground: IWebsiteBuilderSectionBackground = null;

  if (sectionDataBaseContent.background == null) {
    normalizedBackground = defaultBackground;
  } else {
    const { mediaId, src, mediaType, color, size, xPercent, yPercent, darken, darkenPercent, parallax, parallaxAmount } = sectionDataBaseContent.background;

    normalizedBackground = {
      mediaId: websiteBuilderNormalizeDatabaseId(mediaId, defaultBackground.mediaId, false),
      src: websiteBuilderNormalizeValue({
        type: "string",
        value: src,
        defaultValue: defaultBackground.src,
        required: false,
      }),
      mediaType: websiteBuilderNormalizeValue({
        type: "string",
        value: mediaType,
        defaultValue: defaultBackground.mediaType,
        required: true,
        validate: isWebsiteBuilderSectionBackgroundMediaType,
      }) as TWebsiteBuilderSectionBackgroundMediaType,
      color: websiteBuilderNormalizeHexColor(color, defaultBackground.color, false),
      size: websiteBuilderNormalizeValue({
        type: "string",
        value: size,
        defaultValue: defaultBackground.size,
        required: true,
        validate: isWebsiteBuilderSectionBackgroundSize,
      }) as TWebsiteBuilderSectionBackgroundSize,
      xPercent: websiteBuilderNormalizePercent(xPercent, defaultBackground.xPercent, true),
      yPercent: websiteBuilderNormalizePercent(yPercent, defaultBackground.yPercent, true),
      darken: websiteBuilderNormalizeValue({
        type: "boolean",
        value: darken,
        defaultValue: defaultBackground.darken,
        required: true,
      }),
      darkenPercent: websiteBuilderNormalizePercent(darkenPercent, defaultBackground.darkenPercent, true),
      parallax: websiteBuilderNormalizeValue({
        type: "boolean",
        value: parallax,
        defaultValue: defaultBackground.parallax,
        required: true,
      }),
      parallaxAmount: websiteBuilderNormalizePercent(parallaxAmount, defaultBackground.parallaxAmount, true),
    };
  }

  const normalizedSectionDataBaseContent: TWebsiteBuilderSectionDataBaseContent = {
    layout: normalizedLayout,
    background: normalizedBackground,
  };

  return normalizedSectionDataBaseContent;
}

export function websiteBuilderNormalizeSection(
  node: TWebsiteBuilderSection,
  normalizeTraverse: TWebsiteBuilderNormalizeTraverse,
): TWebsiteBuilderSection {
  const children = node.children?.filter(child => {
    if (child?.type === "container") {
      return websiteBuilderContainerValid(child);
    } else if (child?.type === "element") {
      return websiteBuilderElementValid(child);
    } else {
      return false;
    }
  }) ?? [];

  const normalizedSectionDataBaseContent = websiteBuilderNormalizeSectionDataBaseContent({
    layout: node.section.layout,
    background: node.section.background,
  }, children);

  if (isWebsiteBuilderHeader(node)) {
    const normalizedHeader: IWebsiteBuilderHeader = {
      id: node.id,
      type: node.type,
      schemaVersion: node.schemaVersion,
      section: {
        type: node.section.type,
        ...normalizedSectionDataBaseContent,
      },
      children: children.map(child => normalizeTraverse(child)),
    };

    return normalizedHeader;
  } else if (isWebsiteBuilderContent(node)) {
    const normalizedContent: IWebsiteBuilderContent = {
      id: node.id,
      type: node.type,
      section: {
        type: node.section.type,
        ...normalizedSectionDataBaseContent,
        visibility: websiteBuilderNormalizeHideableNodeVisibility(node.section.visibility),
      },
      children: children.map(child => normalizeTraverse(child)),
    };

    return normalizedContent;
  } else if (isWebsiteBuilderFooter(node)) {
    const normalizedFooter: IWebsiteBuilderFooter = {
      id: node.id,
      type: node.type,
      schemaVersion: node.schemaVersion,
      section: {
        type: node.section.type,
        ...normalizedSectionDataBaseContent,
      },
      children: children.map(child => normalizeTraverse(child)),
    };

    return normalizedFooter;
  } else {
    const _exhaustiveCheck: never = node;
    return _exhaustiveCheck;
  }
}

export function websiteBuilderGenerateSectionDesignStyles(
  background: IWebsiteBuilderSectionBackground,
): IWebsiteBuilderNodeDesignStyles {
  const useCssBackgroundImage = Boolean(background.src) && !isWebsiteBuilderSectionBackgroundVideo(background);
  const styleProperties: CSSProperties = {
    backgroundColor: background.color,
    backgroundSize: useCssBackgroundImage ? background.size : null,
    backgroundPositionX: useCssBackgroundImage ? `${background.xPercent}%` : null,
    backgroundPositionY: useCssBackgroundImage ? `${background.yPercent}%` : null,
  };

  let styleDeclaration = "";

  if (styleProperties.backgroundColor) {
    styleDeclaration += `background-color: ${styleProperties.backgroundColor};`;
  }

  if (useCssBackgroundImage) {
    if (styleProperties.backgroundSize) {
      styleDeclaration += `background-size: ${styleProperties.backgroundSize};`;
    }
  
    if (styleProperties.backgroundPositionX) {
      styleDeclaration += `background-position-x: ${styleProperties.backgroundPositionX};`;
    }
  
    if (styleProperties.backgroundPositionY) {
      styleDeclaration += `background-position-y: ${styleProperties.backgroundPositionY};`;
    }
  }

  return {
    className: "website-builder-section",
    styleProperties,
    styleDeclaration,
  };
}

export function websiteBuilderGetSectionScreenPosition(
  leftX: number,
  position: IWebsiteBuilderNodePosition,
): IWebsiteBuilderNodePosition {
  return {
    x: leftX + position.x,
    y: position.y,
  };
}

export function websiteBuilderGetSectionPosition(
  leftX: number,
  screenPosition: IWebsiteBuilderNodePosition,
): IWebsiteBuilderNodePosition {
  return {
    x: screenPosition.x - leftX,
    y: screenPosition.y,
  };
}

export function websiteBuilderGenerateSectionBounds(
  sectionLayout: TWebsiteBuilderSectionLayout,
  device: TWebsiteBuilderDevice,
): IWebsiteBuilderNodeDimensions {
  let width: number = null;

  if (device === "desktop") {
    width = WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_DESKTOP_WIDTH;
  } else if (device === "tablet") {
    width = WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_TABLET_WIDTH;
  } else {
    width = WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_MOBILE_WIDTH;
  }

  return {
    width,
    height: sectionLayout[device].height,
  };
}

export function websiteBuilderGetSectionVisibility(node: TWebsiteBuilderSection, root: IWebsiteBuilderRoot): TWebsiteBuilderHideableNodeVisibility {
  if (node.section.type === "header") {
    return root.headerVisibility;
  } else if (node.section.type === "footer") {
    return root.footerVisibility;
  } else {
    return node.section.visibility;
  }
}